import { trustStats } from "~/data/trust";
import policies from "../server/config/policies.json";

const SITE_URL = "https://georgiadream.ge";

// Coords mirror content/{en,ru,ka}/pages/location.md — update here if that moves.
const COORDS = { lat: 41.7228, lng: 41.7282 } as const;

const AMENITIES = [
  "Swimming Pool",
  // "Parking Nearby", not "Free Parking": on-site guarded parking is paid (per
  // Dreamland price list); only nearby street parking is free. Claiming free
  // parking as a property amenity is misleading structured data.
  "Parking Nearby",
  "Sea View",
  "Beach Access",
  "Elevator",
  "24-Hour Security",
  "Wi-Fi",
] as const;

const IMAGES = [
  `${SITE_URL}/images/complex/complex1.jpg`,
  `${SITE_URL}/images/complex/PoolView.jpg`,
  `${SITE_URL}/images/complex/BeachView.jpg`,
  `${SITE_URL}/images/complex/teritory-v2.jpg`,
];

export type SchemaLocale = "en" | "ru" | "ka";

// Per-locale entity surface. Same @id across all locales → Google dedups to one
// entity; alternateName lists the other locales' variants so the canonical
// brand resolves consistently regardless of which locale Google indexed first.
const NAMES: Record<SchemaLocale, string> = {
  en: "Dreamland Oasis Chakvi Apartments",
  ru: "Апартаменты Dreamland Oasis Chakvi",
  ka: "აპარტამენტები Dreamland Oasis-ში, ჩაქვი",
};

const DESCRIPTIONS: Record<SchemaLocale, string> = {
  en: "Sea-view apartments and studios in Dreamland Oasis Chakvi, Georgia. Direct booking from the owner — verified photos, transparent pricing.",
  ru: "Апартаменты и студии с видом на море в Dreamland Oasis, Чакви. Прямое бронирование от хозяина — реальные фото, прозрачные цены.",
  ka: "ზღვის ხედის აპარტამენტები და სტუდიოები Dreamland Oasis-ში, ჩაქვი. პირდაპირი ჯავშნა მესაკუთრისგან — რეალური ფოტოები, გამჭვირვალე ფასი.",
};

const SCHEMA_LANG: Record<SchemaLocale, string> = {
  en: "en",
  ru: "ru",
  ka: "ka",
};

export interface LodgingBusinessConfig {
  email: string;
  whatsappNumber: string;
  sameAs: string[];
  locale: SchemaLocale;
  // Sourced from /api/apartments/lodging-stats — DB-derived to stay in sync as
  // prices change or units add/remove. Fallbacks keep schema valid if the
  // fetch fails at SSR boot (e.g., DB warmup).
  priceRange?: string;
  numberOfRooms?: number;
}

export function buildLodgingBusinessLd(config: LodgingBusinessConfig) {
  const sameAs = [trustStats.airbnbProfileUrl, ...config.sameAs].filter(
    (u): u is string => typeof u === "string" && u.length > 0
  );

  const alternateName = (Object.keys(NAMES) as SchemaLocale[])
    .filter((l) => l !== config.locale)
    .map((l) => NAMES[l]);

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LodgingBusiness",
        "@id": `${SITE_URL}/#lodgingbusiness`,
        name: NAMES[config.locale],
        alternateName,
        description: DESCRIPTIONS[config.locale],
        inLanguage: SCHEMA_LANG[config.locale],
        url: `${SITE_URL}/`,
        image: IMAGES,
        telephone: config.whatsappNumber,
        email: config.email,
        priceRange: config.priceRange ?? "$$",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Batumi st. 16",
          addressLocality: "Chakvi",
          addressRegion: "Adjara",
          postalCode: "6200",
          addressCountry: "GE",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: COORDS.lat,
          longitude: COORDS.lng,
        },
        hasMap: `https://www.google.com/maps/search/?api=1&query=${COORDS.lat},${COORDS.lng}`,
        numberOfRooms: config.numberOfRooms ?? 13,
        // Single source of truth: server/config/policies.json (also served at /api/policies).
        checkinTime: policies.checkinTime,
        checkoutTime: policies.checkoutTime,
        petsAllowed: policies.petsAllowed,
        smokingAllowed: policies.smokingAllowed,
        amenityFeature: AMENITIES.map((name) => ({
          "@type": "LocationFeatureSpecification",
          name,
          value: true,
        })),
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: trustStats.rating,
          reviewCount: trustStats.reviewsCount,
          bestRating: 5,
          worstRating: 1,
          // reviewCount (54) exceeds the 7 Review nodes embedded inline, so point
          // to the external source (Airbnb host profile) to disambiguate — Google
          // suppresses review snippets on unexplained count mismatches.
          url: trustStats.airbnbProfileUrl,
        },
        sameAs,
        containedInPlace: { "@id": `${SITE_URL}/#organization` },
      },
    ],
  };
}
